import { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, UsersRound } from "lucide-react";
import { toast } from "sonner";

interface Funcionario {
  id: number; nome: string; sCnpjCpf: string; sMatricula: string; idJornada: number; idUsuarioLogin: number; ibAtivo: boolean;
}
interface Jornada { id: number; sNome: string; }

const SEM_JORNADA = "0";
const VAZIO = { Id: 0, Nome: "", SCnpjCpf: "", SMatricula: "", IdJornada: 0, IdUsuarioLogin: 0, IbAtivo: true };

export default function FuncionariosPage() {
  const [itens, setItens] = useState<Funcionario[]>([]);
  const [jornadas, setJornadas] = useState<Jornada[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState<any>(VAZIO);
  const [excluirId, setExcluirId] = useState<number | null>(null);
  const [sel, setSel] = useState<Set<number>>(new Set());
  const [jornadaLote, setJornadaLote] = useState(SEM_JORNADA);

  const jornadaNome = (id: number) => jornadas.find((j) => j.id === id)?.sNome ?? (id > 0 ? `#${id}` : "—");

  const carregar = () => {
    setCarregando(true);
    Promise.all([
      apiClient.get<Funcionario[]>("/api/rh/ponto/funcionarios"),
      apiClient.get<Jornada[]>("/api/rh/ponto/jornadas", { params: { somenteAtivas: true } }),
    ])
      .then(([f, j]) => { setItens(f.data); setJornadas(j.data); })
      .catch((e) => toast.error(e.message ?? "Falha ao carregar."))
      .finally(() => setCarregando(false));
  };
  useEffect(carregar, []);

  const novo = () => { setForm(VAZIO); setDialog(true); };
  const editar = (f: Funcionario) => {
    setForm({ Id: f.id, Nome: f.nome, SCnpjCpf: f.sCnpjCpf, SMatricula: f.sMatricula, IdJornada: f.idJornada, IdUsuarioLogin: f.idUsuarioLogin, IbAtivo: f.ibAtivo });
    setDialog(true);
  };

  const salvar = async () => {
    if (!form.Nome.trim()) { toast.warning("Informe o nome."); return; }
    try {
      await apiClient.post("/api/rh/ponto/funcionarios", form);
      toast.success("Funcionário salvo."); setDialog(false); carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao salvar."); }
  };

  const excluir = async () => {
    if (excluirId == null) return;
    try {
      const r = await apiClient.delete(`/api/rh/ponto/funcionarios/${excluirId}`);
      toast.success(r.data?.mensagem ?? "Excluído."); setExcluirId(null); carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao excluir."); }
  };

  const toggle = (id: number) => setSel((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const atribuirLote = async () => {
    if (sel.size === 0) { toast.warning("Selecione funcionários."); return; }
    try {
      const r = await apiClient.post("/api/rh/ponto/funcionarios/atribuir-jornada", {
        IdJornada: Number(jornadaLote), IdsFuncionarios: [...sel],
      });
      toast.success(r.data?.mensagem ?? "Jornada atribuída.");
      setSel(new Set()); carregar();
    } catch (e: any) { toast.error(e.message ?? "Falha ao atribuir."); }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Funcionários &amp; Escala</h1>
        <Button onClick={novo}><Plus className="h-4 w-4 mr-2" />Novo</Button>
      </div>

      {sel.size > 0 && (
        <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
          <UsersRound className="h-4 w-4" />
          <span className="text-sm">{sel.size} selecionado(s) — atribuir jornada:</span>
          <Select value={jornadaLote} onValueChange={setJornadaLote}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Jornada" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={SEM_JORNADA}>Sem jornada</SelectItem>
              {jornadas.map((j) => <SelectItem key={j.id} value={String(j.id)}>{j.sNome}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={atribuirLote}>Aplicar</Button>
        </div>
      )}

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>CPF</TableHead>
              <TableHead>Matrícula</TableHead>
              <TableHead>Jornada</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {carregando ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Carregando…</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">Nenhum funcionário.</TableCell></TableRow>
            ) : itens.map((f) => (
              <TableRow key={f.id} className={!f.ibAtivo ? "opacity-50" : undefined}>
                <TableCell><Checkbox checked={sel.has(f.id)} onCheckedChange={() => toggle(f.id)} /></TableCell>
                <TableCell>{f.nome}{!f.ibAtivo && <Badge variant="outline" className="ml-2">inativo</Badge>}</TableCell>
                <TableCell>{f.sCnpjCpf || "—"}</TableCell>
                <TableCell>{f.sMatricula || "—"}</TableCell>
                <TableCell>{jornadaNome(f.idJornada)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => editar(f)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => setExcluirId(f.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.Id ? "Editar funcionário" : "Novo funcionário"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1 col-span-2">
              <Label>Nome</Label>
              <Input value={form.Nome} onChange={(e) => setForm({ ...form, Nome: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>CPF</Label>
              <Input value={form.SCnpjCpf} onChange={(e) => setForm({ ...form, SCnpjCpf: e.target.value })} placeholder="000.000.000-00" />
            </div>
            <div className="space-y-1">
              <Label>Matrícula</Label>
              <Input value={form.SMatricula} onChange={(e) => setForm({ ...form, SMatricula: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label>Jornada</Label>
              <Select value={String(form.IdJornada)} onValueChange={(v) => setForm({ ...form, IdJornada: Number(v) })}>
                <SelectTrigger><SelectValue placeholder="Sem jornada" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_JORNADA}>Sem jornada</SelectItem>
                  {jornadas.map((j) => <SelectItem key={j.id} value={String(j.id)}>{j.sNome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Usuário de login (Id) — Portal do Trabalhador</Label>
              <Input type="number" value={form.IdUsuarioLogin} onChange={(e) => setForm({ ...form, IdUsuarioLogin: Number(e.target.value) || 0 })} placeholder="0 = sem portal" />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <Switch checked={form.IbAtivo} onCheckedChange={(v) => setForm({ ...form, IbAtivo: v })} />
              <Label>Ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancelar</Button>
            <Button onClick={salvar}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={excluirId != null} onOpenChange={(o) => !o && setExcluirId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir funcionário?</AlertDialogTitle>
            <AlertDialogDescription>Se houver batidas no histórico, o funcionário é apenas inativado (o ponto é append-only).</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={excluir}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
